import { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import WarningPage from './WarningPage';
import RetryNotice, { RetryInline } from './RetryNotice';
import { departmentLabel } from './departments';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import useWarningMonth from './useWarningMonth';
import WorkingMonthBar from './WorkingMonthBar';
import EffectiveFromField from './EffectiveFromField';
import AppliesCell from './AppliesCell';
import ActiveSwitch from './ActiveSwitch';
import { defaultEffectiveMonth, emptyEffective, startsWithin } from './effectiveWindow';
import { LEAD_CATEGORY_ORDER } from './leadCategories';
import { RULES, numberField, validateNumber } from './thresholdValidation';
import MetricDefinitionNote from './MetricDefinitionNote';
import useConfigAccess from './useConfigAccess';

// Same hardcoded department list as LcrConfig.jsx/AcrConfig.jsx - department
// ids are stable, not a dynamic lookup.
const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];

const DEFAULT_CATEGORY = 'Default';

const emptyForm = {
  department_id: '',
  lead_category: DEFAULT_CATEGORY,
  threshold_value: '',
  is_active: true,
};

const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

// LTA's threshold config screen - same Department + Lead Category model and
// same UI decisions as LcrConfig.jsx/AcrConfig.jsx (name dropdowns, real
// observed categories instead of free text, no operator picker since LTA
// always warns below, % shown on the threshold field, inline add/edit
// instead of a popup dialog). Third identical copy of this shape - see
// lta_config.controller.js's header comment: this is the point to
// generalize, not before.
//
// Lead Category values are shared across metrics - the backfill panel that
// populates them lives on the LCR config screen only.
const LtaConfig = () => {
  const [month, setMonth] = useWarningMonth(); // the month being worked on - passed on to the results screens
  const [rows, setRows] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(''); // '' = every category
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // a blank threshold only complains after the first Save attempt
  const [submitted, setSubmitted] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [categoriesReloadKey, setCategoriesReloadKey] = useState(0); // Retry for just the category list

  const fetchSeq = useRef(0); // each load gets a number; only the newest may update the screen (fast clicks must not show an older answer)
  const fetchRows = ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setRowsLoading(true); // a silent reload (after a switch) keeps the table on screen - no flash
    // apiRequest resolves even on a failed call (src/api/request.js) - this status check is required, a
    // .catch alone would never fire and a server error would silently render as "no thresholds configured".
    return apiRequest({ url: `warning-dashboard/lta-thresholds?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer load replaced this one
        if (res?.status !== 'success') {
          setRowsError(res?.message || 'Could not load LTA thresholds');
          return;
        }
        setRows(res?.data?.rows || []);
        setRowsError(null);
      })
      .catch((err) => {
        if (seq === fetchSeq.current) setRowsError(err?.response?.data?.message || 'Could not load LTA thresholds');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setRowsLoading(false);
      });
  };

  useEffect(() => {
    fetchRows();
  }, [month]); // reload when the working month changes

  useEffect(() => {
    let cancelled = false;
    setCategoriesLoading(true);
    setCategoriesError(null);
    apiRequest({ url: 'warning-dashboard/lead-categories', method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this a failed load left the
        // dropdown silently empty instead of saying so
        if (res?.status !== 'success') {
          setCategoriesError(res?.message || 'Could not load lead categories');
          return;
        }
        setCategories(res?.data?.categories || []);
        setCategoriesError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setCategoriesError(err?.response?.data?.message || 'Could not load lead categories');
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categoriesReloadKey]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, department_id: defaultDepartment(DEPARTMENTS), ...emptyEffective() }); // the month to apply from is read fresh each time the form opens
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      department_id: row.department_id,
      lead_category: row.lead_category,
      threshold_value: row.threshold_value,
      is_active: true, // opening an edit on a switched-off threshold means "use it" - switch it off again below if not
      ...emptyEffective(),
    });
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  // BUG-03: a threshold is a percentage (0-100, never blank), and there is one current threshold per
  // department + lead category - the server enforces both too, this just says so before the request.
  const thresholdField = numberField(form.threshold_value, 'Threshold', RULES.percent, submitted);
  // Several versions of one threshold are normal now (a change from a later month, a one-month override), so only a
  // second ACTIVE OPEN-ENDED row starting in the chosen month or later is a conflict - one that starts earlier is simply closed when the new one begins. Edits and one-month overrides are checked by the server, which knows the months.
  const duplicateOf = editingId ? undefined : rows.find((r) => r.is_active && startsWithin(r, form) && Number(r.department_id) === Number(form.department_id) && r.lead_category === form.lead_category);
  const duplicateError = form.department_id && duplicateOf
    ? `A threshold for ${departmentName(form.department_id)} / ${form.lead_category === DEFAULT_CATEGORY ? 'Default' : form.lead_category} already starts in the chosen month or later - edit it, or choose a later month`
    : '';
  const departmentError = submitted && !form.department_id ? 'Choose a department' : '';

  const handleSave = () => {
    setSubmitted(true);
    if (!form.department_id || duplicateError || validateNumber(form.threshold_value, 'Threshold', RULES.percent)) return;
    setSaving(true);
    const request = editingId
      ? apiRequest({ url: `warning-dashboard/lta-thresholds/${editingId}`, method: 'put', data: form })
      : apiRequest({ url: 'warning-dashboard/lta-thresholds', method: 'post', data: form });

    request
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save this threshold');
          return;
        }
        setFormOpen(false);
        return fetchRows();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save this threshold'))
      .finally(() => setSaving(false));
  };

  const [toggling, setToggling] = useState({}); // rows whose switch request is still in flight - one at a time per row
  const handleToggleActive = (row) => {
    if (toggling[row.id]) return;
    setToggling((t) => ({ ...t, [row.id]: true }));
    const nextActive = !row.is_active;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r)));
    apiRequest({ url: `warning-dashboard/lta-thresholds/${row.id}/active`, method: 'patch', data: { is_active: nextActive, effective_month: defaultEffectiveMonth() } })
      .then((res) => {
        if (res?.status !== 'success') {
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
        } else {
          fetchRows({ silent: true }); // a toggle can start a new version from this month
        }
      })
      .catch(() => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
      })
      .finally(() => setToggling((t) => { const next = { ...t }; delete next[row.id]; return next; }));
  };

  // the categories that actually have a row, Default first then the usual order - the filter offers only these
  const categoryChoices = useMemo(() => {
    const present = [...new Set(rows.map((r) => r.lead_category))];
    const rank = (c) => (c === DEFAULT_CATEGORY ? -1 : LEAD_CATEGORY_ORDER.indexOf(c) === -1 ? LEAD_CATEGORY_ORDER.length : LEAD_CATEGORY_ORDER.indexOf(c));
    return present.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [rows]);
  const activeCategory = categoryChoices.includes(categoryFilter) ? categoryFilter : ''; // a filter whose rows are gone resets itself

  const groupedByDepartment = useMemo(() => {
    const groups = new Map();
    rows.filter((r) => !activeCategory || r.lead_category === activeCategory).forEach((r) => {
      if (!groups.has(r.department_id)) groups.set(r.department_id, []);
      groups.get(r.department_id).push(r);
    });
    groups.forEach((list) =>
      list.sort((a, b) => {
        if (a.lead_category === DEFAULT_CATEGORY && b.lead_category !== DEFAULT_CATEGORY) return -1;
        if (b.lead_category === DEFAULT_CATEGORY && a.lead_category !== DEFAULT_CATEGORY) return 1;
        return a.lead_category.localeCompare(b.lead_category);
      })
    );
    return groups;
  }, [rows, activeCategory]);

  // The same department + lead category more than once (BUG: "duplicate threshold"). The Add form now refuses
  // new ones, but old data can still hold them. Every row of such a pair is tagged; a pair with 2+ ACTIVE rows
  // is a real conflict (the dashboard uses the newest), so that gets a banner.
  const duplicates = useMemo(() => {
    const byKey = new Map();
    rows.forEach((r) => {
      if (r.effective_to) return; // a one-month override or an ended version sits beside the base threshold - not a duplicate of it
      const key = `${r.department_id}|${r.lead_category}`;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(r);
    });
    const alsoIds = new Map(); // row id -> the other ids sharing its department + category
    const activeConflicts = []; // groups with more than one active row
    byKey.forEach((list) => {
      if (list.length < 2) return;
      list.forEach((r) => alsoIds.set(r.id, list.filter((o) => o.id !== r.id).map((o) => o.id)));
      if (list.filter((r) => r.is_active).length > 1) activeConflicts.push(list);
    });
    return { alsoIds, activeConflicts };
  }, [rows]);

  const { defaultDepartment } = useConfigAccess(); // an Add form starts on the person's own department (they can change it)

  return (
    <WarningPage
      title="LTA Threshold Configuration"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard/lta?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
            View Results
          </Button>
          {!formOpen && (
            <Button startIcon={<Icon icon="mynaui:config-solid" width="20" />} variant="contained" onClick={openCreate}>
              Add Threshold
            </Button>
          )}
        </Box>
      }
    >
      <WorkingMonthBar month={month} setMonth={setMonth} />
      <Box sx={{ mb: 3 }}>
        <MetricDefinitionNote metricKey="lta" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Lead categories are captured and backfilled from the{' '}
        <Link to={`/dashboard/warning-dashboard/lcr-config?month=${month.format('YYYY-MM-DD')}`}>LCR Configuration screen</Link>.
      </Typography>

      {formOpen && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            {editingId ? 'Edit Threshold' : 'Add Threshold'}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Every department needs a default threshold; a specific lead category can override it with its own number.
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                select
                label="Department"
                value={form.department_id}
                onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                sx={{ minWidth: 200, flex: 1 }}
                error={!!departmentError}
                helperText={departmentError || undefined}
              >
                {DEPARTMENTS.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Lead Category"
                value={form.lead_category}
                onChange={(e) => setForm({ ...form, lead_category: e.target.value })}
                sx={{ minWidth: 240, flex: 1 }}
                disabled={categoriesLoading || !!categoriesError}
                helperText={
                  categoriesError
                    ? <RetryInline message={categoriesError} loading={categoriesLoading} onRetry={() => setCategoriesReloadKey((k) => k + 1)} />
                    : duplicateError || "\"Default\" sets the department's fallback"
                }
                error={!!categoriesError || !!duplicateError}
                InputProps={
                  categoriesLoading
                    ? { endAdornment: <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment> }
                    : undefined
                }
              >
                <MenuItem value={DEFAULT_CATEGORY}>Default (all other categories)</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Warns below"
                value={form.threshold_value}
                onChange={(e) => setForm({ ...form, threshold_value: e.target.value })}
                sx={{ width: 200 }}
                {...thresholdField.props}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Box>

            <EffectiveFromField form={form} setForm={setForm} />

            <ActiveSwitch form={form} setForm={setForm} offHint="While off, this category uses the department's Default threshold instead (or none, if that is off too)." />

            {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

            <Box display="flex" gap={1}>
              <UpdateButton action={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</UpdateButton>
              <Button variant="text" onClick={closeForm} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {rowsLoading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary">Loading thresholds...</Typography>
        </Box>
      )}

      {!rowsLoading && rowsError && <RetryNotice message={rowsError} onRetry={fetchRows} />}

      {!rowsLoading && !rowsError && duplicates.activeConflicts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          More than one active threshold exists for{' '}
          {duplicates.activeConflicts
            .map((list) => `${departmentName(list[0].department_id)} / ${list[0].lead_category === DEFAULT_CATEGORY ? 'Default' : list[0].lead_category} (#${list.map((r) => r.id).join(', #')})`)
            .join('; ')}
          . Only the newest one is used - switch the others off.
        </Alert>
      )}

      {!rowsLoading && !rowsError && categoryChoices.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <TextField select size="small" label="Lead category" value={activeCategory} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ minWidth: 260 }}>
            <MenuItem value="">All categories</MenuItem>
            {categoryChoices.map((c) => (
              <MenuItem key={c} value={c}>{c === DEFAULT_CATEGORY ? 'Default (all other categories)' : c}</MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {!rowsLoading && !rowsError && rows.length > 0 && groupedByDepartment.size === 0 && (
        <Typography color="text.secondary">No thresholds for this category.</Typography>
      )}

      {!rowsLoading && !rowsError && rows.length === 0 && (
        <Typography color="text.secondary">No thresholds configured yet. Use "Add Threshold" to set one up.</Typography>
      )}

      {!rowsLoading && !rowsError && [...groupedByDepartment.entries()].map(([deptId, deptRows]) => (
        <Box key={deptId} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {departmentName(deptId)}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                  <TableCell><strong>Lead Category</strong></TableCell>
                  <TableCell><strong>Warns below</strong></TableCell>
                  <TableCell><strong>Applies</strong></TableCell>
                  <TableCell><strong>Active</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deptRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.lead_category === DEFAULT_CATEGORY ? (
                        <Chip size="small" label="Default (all other categories)" />
                      ) : (
                        row.lead_category
                      )}
                      {duplicates.alsoIds.has(row.id) && (
                        <Chip size="small" color="warning" label={`Duplicate of #${duplicates.alsoIds.get(row.id).join(', #')}`} sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{row.threshold_value}%</TableCell>
                    <TableCell><AppliesCell row={row} month={month} /></TableCell>
                    <TableCell>
                      <Switch checked={!!row.is_active} disabled={!!toggling[row.id]} onChange={() => handleToggleActive(row)} />
                    </TableCell>
                    <TableCell>
                      <UpdateButton size="small" action={() => openEdit(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </WarningPage>
  );
};

export default LtaConfig;
